import { captureException } from '@sentry/browser';
import axios from 'axios';
import { LOGIN, RESET_PASSWORD, SIGNUP } from 'containers/Routes/paths';
import { KEY_SHOULD_BLOCK_ACCESS } from 'containers/Tools/utils';
import { isMaintenanceModeEnabled } from 'utils/maintenance';
import { HANDLED_STATUSES_CONFIG_KEY, SILENT_CONFIG_KEY } from 'services/api/toastConfig';
import {
  getAccessToken,
  getRefreshToken,
  removeAuthTokens,
  storeAuthTokens,
} from './utils/accessToken';

// You can change the VITE_BASE_URL using your .env.local file:
// After changing it, please remember to stop your development server and start it again.
const baseURL =
  import.meta.env.VITE_BASE_URL ||
  import.meta.env.REACT_APP_BASE_URL;
const clientId =
  import.meta.env.VITE_CLIENT_ID ||
  import.meta.env.REACT_APP_CLIENT_ID;
const exportImageURL = window.env !== 'dev' ? '' : baseURL;

const instance = axios.create({
  baseURL,
});

instance.defaults.headers.common = {
  ...instance.defaults.headers.common,
  'Bfb-React-Version': '1.0',
};


// Attach Authorization Headers
instance.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add a response interceptor for demo projects
instance.interceptors.request.use(request => {
  // If maintenance mode is on, don't do any requests
  if (isMaintenanceModeEnabled()) {
    return Promise.reject(new Error('Maintenance mode is on'));
  }

  const requestUrl = request.url || '';
  const isOauth2Request = requestUrl.includes('/oauth2/');
  const isCredentialsRequest = requestUrl.includes('/api/credentials/');
  const isAuthPage = [LOGIN, SIGNUP, RESET_PASSWORD].includes(
    window.location.pathname.replace(/\/$/, '')
  );

  if (isOauth2Request || isCredentialsRequest || isAuthPage) {
    return request;
  }

  // NOTE: Demo and embedded modes are now controlled via OAuth2 scopes, not URL parameters
  // The backend no longer supports ?demo and ?embedded parameters as of DEV-800 cleanup

  if (sessionStorage.getItem(KEY_SHOULD_BLOCK_ACCESS)) {
    request.params = request.params || {};
    request.params[KEY_SHOULD_BLOCK_ACCESS] = true;
  }

  return request;
});

const axiosBaseQuery =
  ({ apiPath } = { apiPath: '' }) =>
    async ({
      method,
      data,
      params,
      headers,
      url,
      responseType,
      [HANDLED_STATUSES_CONFIG_KEY]: handledStatuses,
      [SILENT_CONFIG_KEY]: silent,
    }) => {
      try {
        const result = await instance({
          url: baseURL + apiPath + (url || ''),
          method,
          data,
          params,
          headers,
          responseType,
          ...(handledStatuses && { [HANDLED_STATUSES_CONFIG_KEY]: handledStatuses }),
          ...(silent && { [SILENT_CONFIG_KEY]: silent }),
        });
        return { data: result.data };
      } catch (axiosError) {
        const err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    };

const isAuthRoute = [LOGIN, SIGNUP, RESET_PASSWORD].includes(
  window.location.pathname.replace(/\/$/, '')
);

const logoutAndRedirectToLogin = () => {
  removeAuthTokens();
  window.location.href = LOGIN;
};

// Capture errors in Sentry
instance.interceptors.response.use(
  response => response,
  error => {
    const sentryUrl = error.config?.url || '';
    if (sentryUrl.includes('sentry.io')) {
      // eslint-disable-next-line no-console
      console.warn('Sentry error ignored:', error.message);
      return Promise.resolve();
    }

    const status = error.response?.status;
    const isNetworkError = error.message === 'Network Error';
    const isTimeout = error.code === 'ECONNABORTED';

    const isExpectedAuthError = isAuthRoute && [400, 401].includes(status);
    const is404Error = status === 404;
    const isExpectedError = status >= 400 && status < 500; // Client errors are often expected

    // Only report to Sentry if it's an unexpected server error or network issue
    if (error.isAxiosError && !isExpectedAuthError && !is404Error && !isExpectedError) {
      captureException(error, {
        tags: {
          isAxiosError: true,
          networkError: isNetworkError,
          timeout: isTimeout,
          statusCode: status,
          axiosUrl: error.config?.url,
          axiosMethod: error.config?.method,
        },
        extra: {
          axiosConfig: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
            timeout: error.config?.timeout,
            data: error.config?.data,
          },
          axiosResponse: {
            status,
            data: error.response?.data,
          },
        },
      });
    }

    return Promise.reject(error);
  }
);

const fetchNewAccessToken = async refreshToken => {
  if (!refreshToken) {
    logoutAndRedirectToLogin();
    return;
  }
  const OAuthQuery = axiosBaseQuery({ apiPath: '/oauth2/' });
  const result = await OAuthQuery({
    method: 'POST',
    url: 'token/', // Backend OAuth2 is NOT under /api/ path
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${refreshToken}`,
    },
    data: {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    },
  });

  if (result.error) {
    // if a new token was requested with the refresh token but couldn't authenticate the user
    logoutAndRedirectToLogin();
  }

  if ('data' in result) {
    return result.data;
  }
};

let refreshTimeoutId = null;

function clearScheduledRefresh() {
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
}

// Scheduling function to get new tokens before the current token expires, executed after login and
// after authentication errors
function scheduleTokenRefresh(refreshToken, expiresIn) {
  clearScheduledRefresh();
  if (!refreshToken || !expiresIn) return;

  const expiresAt = Date.now() + expiresIn * 1000;

  // Remaining time until expiration minus a minute in ms
  const refreshInMs = expiresAt - Date.now() - 60 * 1000;

  const refreshTokenFn = async () => {
    const data = await fetchNewAccessToken(refreshToken);
    if (data) {
      storeAuthTokens(data.access_token, data.refresh_token);
      // Schedule next refresh
      scheduleTokenRefresh(data.refresh_token, data.expires_in);
    }
  };

  // Schedule a refresh if token hasn't expired yet
  if (refreshInMs > 0) {
    refreshTimeoutId = setTimeout(refreshTokenFn, refreshInMs);
  }
}

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const refreshData = await fetchNewAccessToken(refreshToken);
  if (refreshData) {
    storeAuthTokens(refreshData.access_token, refreshData.refresh_token);
    scheduleTokenRefresh(refreshData.refresh_token, refreshData.expires_in);
  }
  return refreshData;
};

let isRefreshing = false;
let refreshPromise = null;
const UNAUTHORIZED = 401;

instance.interceptors.response.use(
  response => response,
  async error => {
    // Capture the failed request
    const originalRequest = error.config;

    // If the request is unauthorized and is not a retry
    if (error.response?.status === UNAUTHORIZED && !originalRequest._retry) {
      // Mark the request as a retry to prevent an infinite loop
      originalRequest._retry = true;

      // If it is the first unauthorized request
      if (!isRefreshing) {
        isRefreshing = true;

        // Fetch a new Access Token using the current refresh token
        const currentRefreshToken = getRefreshToken();
        refreshPromise = fetchNewAccessToken(currentRefreshToken)
          .catch(() => {
            // Logout if could not get a new access token
            if (!isAuthRoute) {
              logoutAndRedirectToLogin();
            }
          })
          .finally(() => {
            // The request for the new access token is over
            isRefreshing = false;
          });
      }

      // Before retrying, await for the request that is trying to obtain a new access token
      // Ensuring to make a single request to refresh the token even when multiple requests failed
      const refreshData = await refreshPromise;
      if (refreshData) {
        // Update the store with new tokens
        storeAuthTokens(refreshData.access_token, refreshData.refresh_token);
        scheduleTokenRefresh(refreshData.refresh_token, refreshData.expires_in);

        // Modify request to be retried with new access token
        originalRequest.headers.Authorization = `Bearer ${refreshData.access_token}`;

        // Retry the failed request with the new token
        return instance(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export {
  axiosBaseQuery,
  baseURL,
  clearScheduledRefresh,
  exportImageURL,
  refreshAccessToken,
  scheduleTokenRefresh,
  logoutAndRedirectToLogin,
};

export default instance;
